import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import { isiOSDevice } from "../../../constants/deviceType";
import { MAX_MESSAGE_LENGTH } from "../../../constants/message-constants";
import MentionAutocomplete from "./MentionAutocomplete";

const InputWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  margin-right: 0.25rem;
`;

const Input = styled.input`
  margin: 0;
  width: 100%;
  filter: none;
  border-radius: 1rem;
  font-size: var(--fs-body);
  background-color: var(--clr-tert);
  color: var(--clr-sec);
  padding: 0.5rem 0.75em;
  border: 0px;
  outline: none;
  line-height: 1;
`;

const InputBoxWithMentions = React.forwardRef((props, ref) => {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const inputRef = useRef();

  // Use forwarded ref or internal ref
  const actualRef = ref || inputRef;

  // Debounce the autocomplete check to reduce performance impact
  const checkAutocomplete = useCallback((text, cursorPos) => {
    // Only check for mentions if there's an @ symbol
    if (!text.includes('@')) {
      setShowAutocomplete(false);
      return;
    }

    const atIndex = text.lastIndexOf('@', cursorPos - 1);
    if (atIndex !== -1) {
      const textAfterAt = text.slice(atIndex + 1, cursorPos);
      const shouldShow = !textAfterAt.includes(' ') && textAfterAt.length >= 0;
      setShowAutocomplete(shouldShow);
    } else {
      setShowAutocomplete(false);
    }
  }, []);

  // Debounce the autocomplete check
  const debouncedAutocompleteCheck = useMemo(() => {
    let timeoutId;
    return (text, cursorPos) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => checkAutocomplete(text, cursorPos), 100);
    };
  }, [checkAutocomplete]);

  useEffect(() => {
    const input = actualRef.current;
    if (!input) return;

    const handleSelectionChange = () => {
      setCursorPosition(input.selectionStart);
    };

    const handleInput = () => {
      const newCursorPos = input.selectionStart;
      setCursorPosition(newCursorPos);
      
      // Only check for autocomplete if we might need it
      const text = input.value;
      if (text.includes('@')) {
        debouncedAutocompleteCheck(text, newCursorPos);
      } else {
        setShowAutocomplete(false);
      }
    };

    // Use passive listeners for better performance
    input.addEventListener('input', handleInput);
    input.addEventListener('keyup', handleSelectionChange, { passive: true });
    input.addEventListener('click', handleSelectionChange, { passive: true });

    return () => {
      input.removeEventListener('input', handleInput);
      input.removeEventListener('keyup', handleSelectionChange);
      input.removeEventListener('click', handleSelectionChange);
    };
  }, [actualRef, debouncedAutocompleteCheck]);

  const handleSelectUser = (user, mentionStart, mentionLength) => {
    const input = actualRef.current;
    const currentText = input.value;
    
    // Replace the @query with @nickname
    const beforeMention = currentText.slice(0, mentionStart);
    const afterMention = currentText.slice(mentionStart + mentionLength);
    const newText = `${beforeMention}@${user.nickname} ${afterMention}`;
    
    // Update input value
    props.onChange(newText);
    
    // Set cursor position after the mention
    setTimeout(() => {
      const newCursorPos = mentionStart + user.nickname.length + 2; // +2 for @ and space
      input.setSelectionRange(newCursorPos, newCursorPos);
      setCursorPosition(newCursorPos);
    }, 0);
    
    setShowAutocomplete(false);
  };

  const handleCloseAutocomplete = () => {
    setShowAutocomplete(false);
  };

  const handleKeyDown = (e) => {
    // Only prevent default behavior for autocomplete navigation keys when autocomplete is showing
    if (showAutocomplete && ['ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      return;
    }
    
    // Let all other keys (including backspace, enter, tab, escape) work normally
  };

  return (
    <InputWrapper>
      {showAutocomplete && (
        <MentionAutocomplete
          inputText={props.value}
          cursorPosition={cursorPosition}
          messages={props.messages || []}
          onSelectUser={handleSelectUser}
          onClose={handleCloseAutocomplete}
        />
      )}
      <Input
        autoFocus={isiOSDevice() ? false : true}
        autoComplete="off"
        autoCorrect="off"
        ref={actualRef}
        placeholder="What's on your mind?"
        maxLength={MAX_MESSAGE_LENGTH}
        tabIndex="-1"
        disabled={props.disabled}
        value={props.value}
        onChange={(event) => {
          props.onChange(event.target.value);
        }}
        onKeyDown={handleKeyDown}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
      />
    </InputWrapper>
  );
});

export default InputBoxWithMentions;